import React from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';

import { useHistoryPageState } from './hooks/useHistoryPageState';
import {
  HistoryAssessment,
  HistoryCalibrationTests,
  HistoryCustomerSignoff,
  HistoryHeader,
  HistoryPhotos,
  HistoryResultsSummary
} from './components';

type StatusMessageProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
};

const StatusMessage: React.FC<StatusMessageProps> = ({
  icon,
  title,
  description
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 'calc(100vh - 64px)'
    }}
  >
    <Stack spacing={3} alignItems="center" textAlign="center" maxWidth="sm">
      {icon}
      <Typography variant="h4" fontWeight={600}>
        {title}
      </Typography>
      <Typography variant="body1" color="text.secondary" maxWidth="400px">
        {description}
      </Typography>
    </Stack>
  </Box>
);

const HistoryPage: React.FC = () => {
  const { dcRuntimeId } = useParams<{ dcRuntimeId: string }>();
  const { t } = useTranslation('history');
  const theme = useTheme();

  const {
    isLoading,
    error,
    calibrationRuntime,
    calibrationData,
    workOrderDetail,
    isCompleted,
    hasData
  } = useHistoryPageState(dcRuntimeId);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 'calc(100vh - 64px)'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || (!isLoading && !calibrationRuntime)) {
    return (
      <StatusMessage
        icon={
          <ErrorOutlineOutlinedIcon
            sx={{ fontSize: 80, color: theme.palette.error.main }}
          />
        }
        title={t('notFound')}
        description={t('notFoundDescription')}
      />
    );
  }

  if (!hasData) {
    return (
      <StatusMessage
        icon={
          <InboxOutlinedIcon
            sx={{ fontSize: 80, color: theme.palette.text.disabled }}
          />
        }
        title={t('noData')}
        description={t('noDataDescription')}
      />
    );
  }

  if (!isCompleted) {
    return (
      <StatusMessage
        icon={
          <HourglassEmptyIcon
            sx={{ fontSize: 80, color: theme.palette.warning.main }}
          />
        }
        title={t('notCompleted')}
        description={t('notCompletedDescription')}
      />
    );
  }

  if (!calibrationData || !calibrationRuntime) return null;

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 3
      }}
    >
      <HistoryHeader
        calibrationData={calibrationData}
        calibrationRuntime={calibrationRuntime}
        workOrderDetail={workOrderDetail}
      />
      <HistoryCalibrationTests calibrationData={calibrationData} />
      <HistoryAssessment assessment={calibrationData.assessment} />
      <HistoryResultsSummary calibrationData={calibrationData} />
      <HistoryPhotos eventNum={calibrationRuntime.eventNum} />
      {calibrationData.customerSignoff && (
        <HistoryCustomerSignoff signoff={calibrationData.customerSignoff} />
      )}
    </Box>
  );
};

export default HistoryPage;
